import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { Shield } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useAdminAuth } from '~/entities/session/model/use-admin-auth'
import { useLogin } from '~/entities/session/model/use-auth'
import { AuthHeading, AuthShell, authLinkClassName } from '~/features/auth/auth-shell'
import * as m from '~/paraglide/messages'
import { createFieldValidators } from '~/shared/lib/form-utils'
import { adminLoginSchema } from '~/shared/types/admin'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { PasswordInput } from '~/shared/ui/password-input'
import { version } from '../../package.json'

export const Route = createFileRoute('/admin-login')({
  component: AdminLoginPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      email: (search.email as string) || '',
      reason: (search.reason as string) || '',
    }
  },
})

function AdminLoginPage() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAdminAuth()
  const login = useLogin({ requireAdmin: true })
  const { email: searchEmail, reason } = Route.useSearch()

  const toastShown = useRef(false)
  useEffect(() => {
    if (toastShown.current) return
    if (reason === 'forbidden') {
      toastShown.current = true
      toast.error(m.accessDeniedPleaseLogInAgain(), {
        duration: 4000,
      })
      window.history.replaceState({}, '', '/admin-login')
    } else if (reason === 'expired') {
      toastShown.current = true
      toast.error(m.sessionExpiredPleaseLogInAgain(), {
        duration: 4000,
      })
      window.history.replaceState({}, '', '/admin-login')
    }
  }, [reason])

  const form = useForm({
    defaultValues: {
      email: searchEmail || '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      await login.mutateAsync(value)
    },
  })

  const formValues = useStore(form.store, (state) => state.values)
  const emailValue = (formValues.email ?? '').trim()
  const passwordValue = (formValues.password ?? '').trim()
  const isSubmitDisabled = login.isPending || !emailValue || !passwordValue

  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="inline-block border-4 border-muted-foreground border-r-transparent border-solid rounded-full w-8 h-8 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite] align-[-0.125em]" />
        </div>
      </div>
    )
  }
  if (isAuthenticated) {
    return <Navigate to="/admin" />
  }

  return (
    <AuthShell
      className="beacon-admin"
      footer={
        <>
          <p>
            {m.notAnAdmin()}{' '}
            <Link to="/login" search={{ email: '', reason: '' }} className={authLinkClassName}>
              {m.businessLogin()}
            </Link>
          </p>
          <p className="text-xs">
            {m.version()} {version}
          </p>
        </>
      }
    >
      <AuthHeading
        icon={<Shield />}
        title={m.adminPortal()}
        description={m.secureAccessForAdministrators()}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-4"
      >
        <form.Field name="email" validators={createFieldValidators(adminLoginSchema.shape.email)}>
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.email()}</Label>
              <Input
                id={field.name}
                type="email"
                placeholder="admin@example.com"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {field.state.meta.isTouched && field.state.meta.errors?.[0] && (
                <p className="text-destructive text-sm">
                  {typeof field.state.meta.errors[0] === 'string'
                    ? field.state.meta.errors[0]
                    : m.invalidValue()}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="password"
          validators={createFieldValidators(adminLoginSchema.shape.password)}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.password()}</Label>
              <PasswordInput
                id={field.name}
                placeholder="••••••••"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSubmitDisabled) {
                    e.preventDefault()
                    form.handleSubmit()
                  }
                }}
              />
              {field.state.meta.isTouched && field.state.meta.errors?.[0] && (
                <p className="text-destructive text-sm">
                  {typeof field.state.meta.errors[0] === 'string'
                    ? field.state.meta.errors[0]
                    : m.invalidValue()}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            search={{ email: emailValue }}
            className="text-muted-foreground text-sm transition-colors hover:text-foreground"
          >
            {m.forgotPassword()}
          </Link>
        </div>

        {login.isError && (
          <div className="squircle rounded-lg border border-destructive/20 bg-destructive/10 p-3">
            <p className="text-destructive text-sm">
              {login.error instanceof Error
                ? login.error.message
                : m.failedToLoginPleaseCheckYourCredentials()}
            </p>
          </div>
        )}

        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="w-full"
          disabled={isSubmitDisabled}
        >
          {login.isPending ? m.signingIn() : m.signInAsAdmin()}
        </Button>
      </form>
    </AuthShell>
  )
}
